"use client";

import { useParams } from "next/navigation";
import { useState, useEffect, Suspense } from "react";
import Image from "next/image";
import { ArrowLeft, Star, MapPin, Award, Building2, ChevronRight, SearchX, Heart } from "lucide-react";
import Link from "next/link";
import { useI18n } from "@/i18n/I18nProvider";
import Header from "@/components/layout/Header";
import PageHeader from "@/components/layout/PageHeader";
import EmptyState from "@/components/ui/EmptyState";
import DemoNote from "@/components/ui/DemoNote";
import { getHospitalById } from "@/services/mockService";
import { useFavorites } from "@/hooks/useFavorites";
import type { Hospital } from "@/data/mockData";

function HospitalDetailContent() {
  const { t } = useI18n();
  const params = useParams();
  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [loading, setLoading] = useState(true);
  const { isFavorite, toggleFavorite } = useFavorites();

  useEffect(() => {
    if (params.id) {
      getHospitalById(Number(params.id)).then((data) => { setHospital(data ?? null); setLoading(false); });
    }
  }, [params.id]);

  if (loading) {
    return (
      <div className="pb-24">
        <Header />
        <div className="px-5 pt-3 space-y-3">
          <div className="cc-skeleton h-[200px]" />
          <div className="cc-skeleton h-[156px]" />
          <div className="cc-skeleton h-[132px]" />
        </div>
      </div>
    );
  }

  if (!hospital) {
    return (
      <div className="pb-24">
        <PageHeader title={t("Hospital Details")} backHref="/hospitals" />
        <EmptyState icon={SearchX} title={t("Hospital not found")} hint={t("This hospital may no longer be listed.")} />
      </div>
    );
  }

  return (
    <div className="pb-24">
      <Header />

      {/* Hero */}
      <div className="relative h-[220px] bg-primary-bg overflow-hidden">
        <Image
          src={hospital.image}
          alt={hospital.name}
          fill
          sizes="(max-width: 430px) 100vw, 430px"
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/35" />
        <Link href="/hospitals" aria-label={t("Back")}
          className="absolute top-4 left-5 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm">
          <ArrowLeft size={18} className="text-text-dark" />
        </Link>
        {hospital.isTop && (
          <span className="absolute top-4 left-16 bg-amber-400 text-[11px] font-bold text-white px-2.5 py-1 rounded-full shadow-sm">{t("Top Hospital")}</span>
        )}
        <button
          aria-label={isFavorite(hospital.id) ? t("Remove from favorites") : t("Add to favorites")}
          onClick={() => toggleFavorite(hospital.id)}
          className="absolute top-4 right-5 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm"
        >
          <Heart size={17} className={isFavorite(hospital.id) ? "fill-red-500 text-red-500" : "text-text-dark"} />
        </button>
        <h1 className="absolute left-5 right-5 bottom-7 text-[20px] font-extrabold leading-[1.25] text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.35)]">
          {hospital.name}
        </h1>
      </div>

      <div className="px-5 -mt-4">
        <DemoNote text={t("Demo hospital notice")} className="mx-0 mb-3 mt-0" />
        <div className="cc-card p-4 mb-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <MapPin size={14} className="text-text-dark/40" />
            <span className="text-[13px] text-text-dark/55">{hospital.city}</span>
            <span className="text-text-dark/20">|</span>
            <Star size={13} className="fill-amber-400 text-amber-400" />
            <span className="text-[13px] font-semibold text-text-dark">{hospital.rating}</span>
            <span className="text-[12px] text-text-dark/45">({hospital.reviews} {t("reviews")})</span>
          </div>
          <p className="mt-2.5 text-[14px] text-text-dark/65 leading-[1.6]">{hospital.description}</p>
        </div>

        <div className="cc-card p-4 mb-3">
          <h2 className="cc-section-title mb-3 flex items-center gap-2">
            <Award size={16} className="text-primary" /> {t("Accreditation")}
          </h2>
          <div className="flex flex-wrap gap-2">
            {hospital.accreditation.map((a) => (
              <span key={a} className="text-[12.5px] bg-primary-bg text-primary px-2.5 py-1 rounded-lg font-medium">{a}</span>
            ))}
          </div>
        </div>

        <div className="cc-card p-4 mb-3">
          <h2 className="cc-section-title mb-3 flex items-center gap-2">
            <Building2 size={16} className="text-primary" /> {t("Departments")}
          </h2>
          <div className="flex flex-wrap gap-2">
            {hospital.departments.map((d) => (
              <span key={d} className="text-[12.5px] bg-page-bg text-text-dark/65 px-2.5 py-1 rounded-lg">{d}</span>
            ))}
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4">
          <p className="text-[12.5px] text-amber-700 leading-[1.6]">
            {t("Hospital information is for reference only. Availability and services are subject to hospital confirmation.")}
          </p>
        </div>

        <Link href={`/book?hospitalId=${hospital.id}`} className="block">
          <button className="cc-btn">
            {t("Request Service at This Hospital")}
            <ChevronRight size={17} />
          </button>
        </Link>
      </div>
    </div>
  );
}

export default function HospitalDetailPage() {
  return (
    <Suspense fallback={<div className="pb-24 px-5 pt-4"><div className="cc-skeleton h-[200px]" /></div>}>
      <HospitalDetailContent />
    </Suspense>
  );
}
